import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ResetPassword } from 'src/app/shared/interfaces/reset-password.inerface';
import { PasswordService } from 'src/app/shared/services/password.service';
import { TurnstileService } from 'src/app/shared/services/turnstile.service';
import { TurnstileComponent } from 'src/app/shared/components/turnstile/turnstile.component';
import { ValidateNotEmptyInput } from 'src/app/shared/validators/is-not-empty.validator';
import { UIIconsEnum } from 'src/app/shared/enums/icons.enum';
import { environment } from 'src/environments/environment';

declare var bootstrap: any;

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
})
export class ResetPasswordComponent implements OnInit, AfterViewInit {
  @ViewChild('turnstileWidget') turnstileWidget!: TurnstileComponent;

  private token!: string;
  private email!: string;

  resetPasswordForm!: FormGroup;
  successMessage!: string;
  showSuccess = false;
  errorMessage!: string;
  showError = false;
  isLoading = false;
  turnstileToken: string | null = null;
  siteKey = environment.turnstileSiteKey;

  // Password visibility toggles
  passwordTypeText = false;
  confirmPasswordTypeText = false;

  constructor(
    private formBuilder: FormBuilder,
    private passwordService: PasswordService,
    private route: ActivatedRoute,
    private turnstileService: TurnstileService,
  ) {}

  ngOnInit(): void {
    this.resetPasswordForm = this.formBuilder.group({
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(32),
          ValidateNotEmptyInput,
          Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/),
          this.noSpacesValidator
        ],
      ],
      confirmPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(32),
          ValidateNotEmptyInput,
          this.noSpacesValidator
        ],
      ],
    });

    // Add value change listeners to trigger password match validation
    this.resetPasswordForm.get('password')?.valueChanges.subscribe(() => {
      this.resetPasswordForm.get('confirmPassword')?.updateValueAndValidity();
    });

    this.resetPasswordForm.get('confirmPassword')?.setValidators([
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(32),
      ValidateNotEmptyInput,
      this.noSpacesValidator,
      this.passwordMatchValidator.bind(this)
    ]);

    this.route.queryParams.subscribe((params) => {
      this.token = params['token'];
      this.email = params['email'];
    });
  }

  ngAfterViewInit(): void {
    // Initialize tooltips
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.map(function (tooltipTriggerEl) {
      return new bootstrap.Tooltip(tooltipTriggerEl);
    });
  }

  // Toggle password visibility methods
  togglePasswordTextType(): void {
    this.passwordTypeText = !this.passwordTypeText;
  }

  toggleConfirmPasswordTextType(): void {
    this.confirmPasswordTypeText = !this.confirmPasswordTypeText;
  }

  getVisibilityIcon(isVisible: boolean) {
    return isVisible ? UIIconsEnum.Show : UIIconsEnum.Hide;
  }

  hasError(controlName: string, errorName: string) {
    const control = this.resetPasswordForm.controls[controlName];
    return control.touched && control.hasError(errorName);
  }

  // Password validation helper methods
  hasMinLength(): boolean {
    const password = this.resetPasswordForm.get('password')?.value;
    return password?.length >= 8;
  }

  hasUpperCase(): boolean {
    const password = this.resetPasswordForm.get('password')?.value;
    return /[A-Z]/.test(password || '');
  }

  hasLowerCase(): boolean {
    const password = this.resetPasswordForm.get('password')?.value;
    return /[a-z]/.test(password || '');
  }

  hasNumber(): boolean {
    const password = this.resetPasswordForm.get('password')?.value;
    return /[0-9]/.test(password || '');
  }

  hasSpecialChar(): boolean {
    const password = this.resetPasswordForm.get('password')?.value;
    return /[!@#$%^&*(),.?":{}|<>]/.test(password || '');
  }

  hasSpaces(): boolean {
    const password = this.resetPasswordForm.get('password')?.value;
    return /\s/.test(password || '');
  }

  noSpacesValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const hasSpaces = /\s/.test(control.value);
    return hasSpaces ? { 'containsSpaces': true } : null;
  }

  passwordMatchValidator(control: any): { [key: string]: boolean } | null {
    if (!this.resetPasswordForm) {
      return null;
    }

    const password = this.resetPasswordForm.get('password')?.value;
    const confirmPassword = control.value;

    if (password !== confirmPassword) {
      return { 'mismatch': true };
    }

    return null;
  }

  onTurnstileToken(token: string | null): void {
    this.turnstileToken = token;
    this.turnstileService.setToken(token);
  }

  resetPassword() {
    if (this.resetPasswordForm.valid && this.turnstileToken) {
      this.showError = false;
      this.showSuccess = false;
      this.isLoading = true;

      const resetPassword: ResetPassword = {
        password: this.resetPasswordForm.get('password')?.value,
        confirmPassword: this.resetPasswordForm.get('confirmPassword')?.value,
        email: this.email,
        token: this.token,
      };

      this.passwordService.resetPassword(resetPassword).subscribe({
        next: () => {
          this.showSuccess = true;
          this.resetPasswordForm.reset();
          this.turnstileWidget?.reset();
          this.turnstileService.clearToken();
          this.turnstileToken = null;
        },
        error: (err: HttpErrorResponse) => {
          this.showError = true;
          if (err.error && err.error.messages && err.error.messages.length > 0) {
            this.errorMessage = err.error.messages
              .map((msg: any) => msg.content)
              .filter((content: string | undefined) => content)
              .join(', ');
            this.isLoading = false;
          } else if (err.error && typeof err.error === 'string') {
            this.errorMessage = 'Something went wrong, please try again later.';
            this.isLoading = false;
          } else {
            this.errorMessage = err.message || 'An error occurred while resetting your password.';
            this.isLoading = false;
          }

          if (err.status === 400 || err.status === 403) {
            this.turnstileWidget?.reset();
            this.turnstileService.clearToken();
            this.turnstileToken = null;
          }
        },
        complete: () => {
          this.isLoading = false;
        }
      });
    }
  }
}
